import { BaseInput } from '@components/components_base/base_input.component';
import { Icon } from '@components/icon.component';
import { getAbsoluteRoute } from '@helpers/route';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { useComboBox } from '@react-aria/combobox';
import { useFilter } from '@react-aria/i18n';
import { Item } from '@react-stately/collections';
import { useComboBoxState } from '@react-stately/combobox';
import { useAsyncList } from '@react-stately/data';
import type { ComboBoxProps } from '@react-types/combobox';
import type { LoadingState } from '@react-types/shared';
import { LicenceHolderService } from '@services/licence_holder.service';
import { RouterService } from '@services/router.service';
import { theme } from '@services/theme.service/theme';
import { LicenceHolderApiService } from '@src/api/licence_holder_api.service';
import type { HttpCancelTokenSource } from '@visiba/http';
import { Http } from '@visiba/http';
import { Scoped, useService } from '@visiba/instantiation';
import { rem } from '@visiba/std';
import { AnimatePresence } from 'framer-motion';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { BROWSE_KEY, SearchUnitNode } from './_common';
import { ListBox } from './_list_box.component';
import { Popover } from './_popover.component';

interface Props {
	placeholder?: string;
	includeBrowseInResult: boolean;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onOpenChange?: (isOpen: boolean) => void;
	onNodeSelected?: (node: SearchUnitNode) => void;
}

interface Autocomplete<T> extends ComboBoxProps<T> {
	loadingState?: LoadingState;
	onLoadMore?: () => void;
}

const elements = {
	label: styled.label`
		margin-bottom: 10px;
		display: block;
	`,
	container: styled.div`
		position: relative;
		width: 100%;
	`,
	iconArea: styled.div`
		grid-area: iconArea;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: ${rem(20)};
		color: ${theme.primary};
	`,
	inputContainer: styled.div`
		display: grid;
		grid-column-gap: 0;
		grid-row-gap: 0;
		grid-template-areas: 'iconArea inputArea';
		grid-template-columns: ${rem(40)} auto;
		border: ${rem(1)} solid ${theme.primary};
		width: 100%;
		border-radius: 9999px;
		background: #ffffff;
		padding-right: ${rem(15)};
		min-height: ${rem(36)}px;
		width: 100%;
	`,
	inputArea: styled(BaseInput)`
		color: #000;
		font-style: normal;
		font-weight: normal;
		line-height: ${rem(22)};
		grid-area: inputArea;
		display: flex;
		align-items: center;
		text-overflow: ellipsis;
	`,
};

// Reason: React-Aria is a bit bad in the type department. Not sure why they're
// not allowing Record<K, V>. Can be due to enumerable logics but still...
// eslint-disable-next-line @typescript-eslint/ban-types
function Autocomplete(props: Autocomplete<SearchUnitNode>): JSX.Element {
	const { contains } = useFilter({ sensitivity: 'base' });
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	const state = useComboBoxState<any>({
		// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
		...(props as any) /* Blame @emilnordling, we should use Cellula anyhow... */,
		defaultFilter: contains,
	});

	const inputRef = useRef(null);
	const listBoxRef = useRef(null);
	const popoverRef = useRef(null);

	const { inputProps, listBoxProps } = useComboBox(
		{
			...props,
			inputRef,
			listBoxRef,
			popoverRef,
			// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
		} as any /* Blame @emilnordling, we should use Cellula anyhow... */,
		state,
	);

	return (
		<elements.container>
			<elements.inputContainer>
				<elements.iconArea>
					<Icon type='search' />
				</elements.iconArea>
				{/* Reason: Miss-match with React-Aria and Styled Components... */}
				{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
				{/** biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed */}
				<elements.inputArea {...(inputProps as any)} ref={inputRef} placeholder={props.placeholder} />
			</elements.inputContainer>

			<AnimatePresence>
				{props.inputValue && props.inputValue.length >= 2 ? (
					<Popover popoverRef={popoverRef} isOpen={state.isOpen} onClose={state.close}>
						<ListBox
							{...listBoxProps}
							listBoxRef={listBoxRef}
							// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
							state={state as any}
							loadingState={props.loadingState}
							onLoadMore={props.onLoadMore}
						/>
					</Popover>
				) : null}
			</AnimatePresence>
		</elements.container>
	);
}

export function SearchReceptionComboBox({ placeholder, includeBrowseInResult, onFocus, onNodeSelected }: Props): JSX.Element {
	const routerService = useService(RouterService);
	const licenceHolderService = useService(LicenceHolderService);
	const controller = useService(SearchReceptionController);

	const list = useAsyncList<SearchUnitNode>({
		async load({ filterText }) {
			return await controller.search(filterText ?? '', includeBrowseInResult);
		},
	});

	function handleChange(key: React.Key | null): void {
		function __TEMP__navigation(to: string): void {
			if (window.__TEMP__overrideNavigation) {
				window.__TEMP__overrideNavigation(to);

				return;
			}

			routerService.navigateByUrl(to);
		}

		if (key == null) return;
		const node = list.items.find((item) => item.name === key);
		if (node == null) return;

		if (onNodeSelected != null) {
			onNodeSelected(node);
		}

		const route = node.id === BROWSE_KEY ? `${licenceHolderService.alias}/change-unit` : node.path;

		__TEMP__navigation(getAbsoluteRoute(route));
	}

	return (
		<Autocomplete
			placeholder={placeholder ?? I18nAndL10n.translate('Patient.DefaultHomeGuiComponents.Search')}
			items={list.items}
			inputValue={list.filterText}
			onInputChange={list.setFilterText}
			onSelectionChange={handleChange}
			loadingState={list.loadingState}
			onLoadMore={list.loadMore}
			onFocus={onFocus}
		>
			{(item) => <Item key={item.name}>{item.name}</Item>}
		</Autocomplete>
	);
}

@Scoped()
class SearchReceptionController {
	private prevRequest: HttpCancelTokenSource | null = null;

	constructor(
		private readonly licenceHolderService: LicenceHolderService,
		private readonly licenceHolderApiService: LicenceHolderApiService,
	) {
		// Empty
	}

	public async search(text: string, includeBrowseInResult: boolean): Promise<{ items: SearchUnitNode[] }> {
		if (text.length < 2) {
			return {
				items: [],
			};
		}

		if (this.prevRequest != null) this.prevRequest.cancel();
		this.prevRequest = Http.createCancelTokenSource();

		const { data } = await this.licenceHolderApiService.search(this.licenceHolderService.id, text, this.prevRequest);

		this.prevRequest = null;

		let items: SearchUnitNode[] = data.map((item) => ({
			id: item.Path,
			name: item.Name,
			path: item.Path,
			subtitle: item.Subtitle,
		}));

		if (includeBrowseInResult) {
			items = [
				...items,
				{
					id: BROWSE_KEY,
					name: I18nAndL10n.translate('Patient.DefaultHomeGuiComponents.Browse'),
					path: '',
					subtitle: null,
				},
			];
		}

		return {
			items,
		};
	}
}
